/*
 * docs.orvian.mx abre con la sesión del correo — la MISMA, no una copia ni una sesión nueva.
 *
 * El portal guarda la sesión en localStorage, que es de cada origen: docs no ve la del correo.
 * Así que docs carga, oculta, una página mínima del correo (public/sesion-docs.html) y se la pide
 * por postMessage. Esa página solo contesta a docs.orvian.mx y solo con las claves de abajo.
 *
 *   docs → correo   pedir                 (al arrancar)
 *   correo → docs   foto {claves}         (lo que hay ahora en el correo)
 *   correo → docs   cambio {clave, valor} (otra pestaña del correo renovó el token, cambió el tema, cerró sesión…)
 *   docs → correo   escribir {clave, valor}
 *
 * Las claves se reflejan en el localStorage de docs, así que api.js y el store siguen leyendo
 * donde siempre. Y lo que docs escribe en ellas se reenvía al correo: para esas claves, las dos
 * pestañas se comportan como si compartieran almacenamiento (que es como funcionaba cuando el
 * editor vivía en el mismo dominio). Por qué funciona el iframe: los dos dominios son el mismo
 * SITIO (orvian.mx), y los navegadores no parten el almacenamiento de un iframe del mismo sitio.
 * Si aun así falla, se devuelve a la persona al correo y el editor abre allí (ver dominios.js).
 */
import { CORREO_URL, enMeet } from './dominios';

/* meet.orvian.mx usa el mismo puente con otro nombre de archivo: nginx sirve /sesion-docs.html con
   `frame-ancestors https://docs.orvian.mx`, así que Meet no puede enmarcarlo (ver public/sesion-meet.html). */
const PUENTE = () => (enMeet() ? '/sesion-meet.html' : '/sesion-docs.html');

export const CLAVES_SESION = [
  'zuno_access', 'zuno_refresh', 'zuno_user', 'zuno_deleg', // sesión (y acceso delegado de un admin)
  'zuno_theme', 'zuno_office_editor', // preferencias que el editor lee y cambia
];

const nativo = { set: Storage.prototype.setItem, remove: Storage.prototype.removeItem };
let marco = null;

/* D1 (auditoría 2026-10-05): una pestaña de docs YA ABIERTA conserva la identidad con la que se abrió.
   Antes, si en el correo un admin entraba al buzón de otra persona, la pestaña del documento pasaba a
   usar ese token delegado (sin refresh) y a los 60 min la sacaba del documento. Se aceptan los tokens
   renovados de la misma persona y el cierre de sesión; se ignora el cambio a otra cuenta o a una delegación. */
const CLAVES_IDENTIDAD = ['zuno_access', 'zuno_refresh', 'zuno_user', 'zuno_deleg'];
let titular = null;   // `sub` del token con el que se abrió esta pestaña
let delegacionAjena = false;   // el correo está ahora en el buzón de otra persona

function subDe(token) {
  try {
    return JSON.parse(atob(String(token).split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).sub || null;
  } catch {
    return null;
  }
}

/** ¿Este cambio cambiaría de persona a una pestaña que ya tiene la suya? */
function cambiaDePersona(clave, valor) {
  if (!titular || !CLAVES_IDENTIDAD.includes(clave) || valor === null || valor === undefined) return false;
  if (clave === 'zuno_deleg') return true;
  if (clave === 'zuno_access') return subDe(valor) !== titular;
  if (clave === 'zuno_user') {
    try { return (JSON.parse(valor) || {}).id !== titular; } catch { return false; }
  }
  return false;   // zuno_refresh: la delegación lo borra (null), nunca trae uno de otra persona
}

function reflejar(clave, valor) {
  // Con los métodos originales: lo que llega del correo no se le vuelve a mandar
  try {
    if (valor === null || valor === undefined) nativo.remove.call(window.localStorage, clave);
    else nativo.set.call(window.localStorage, clave, String(valor));
  } catch {
    /* sin localStorage: se queda sin reflejar */
  }
}

function enviar(mensaje) {
  try {
    marco?.contentWindow?.postMessage(mensaje, CORREO_URL);
  } catch {
    /* el iframe ya no está */
  }
}

function reenviarEscrituras() {
  const propio = window.localStorage;
  Storage.prototype.setItem = function setItem(clave, valor) {
    nativo.set.call(this, clave, valor);
    if (this === propio && CLAVES_SESION.includes(clave)) {
      enviar({ tipo: 'orvian-sesion:escribir', clave, valor: String(valor) });
    }
  };
  Storage.prototype.removeItem = function removeItem(clave) {
    nativo.remove.call(this, clave);
    if (this === propio && CLAVES_SESION.includes(clave)) {
      enviar({ tipo: 'orvian-sesion:escribir', clave, valor: null });
    }
  };
}

/**
 * Trae la sesión del correo antes de pintar nada. Resuelve true si el correo contestó (haya o no
 * sesión) y false si no contestó a tiempo; en ese caso borra lo que docs tuviera guardado, para no
 * abrir con una sesión vieja que el correo quizá ya cerró.
 * `alCambiar(clave, valor)` avisa de los cambios que lleguen después.
 */
export function conectarSesionCorreo({ espera = 8000, alCambiar } = {}) {
  return new Promise((resolve) => {
    let conectado = false;
    let pedido = false;

    const pedir = () => {
      if (pedido) return;
      pedido = true;
      enviar({ tipo: 'orvian-sesion:pedir' });
    };

    const alMensaje = (e) => {
      if (!marco || e.source !== marco.contentWindow || e.origin !== CORREO_URL) return;
      const d = e.data || {};
      if (d.tipo === 'orvian-sesion:lista') {
        pedir();
      } else if (d.tipo === 'orvian-sesion:foto') {
        const claves = d.claves || {};
        const otraPersona = conectado && (claves.zuno_deleg || CLAVES_IDENTIDAD.some((c) => cambiaDePersona(c, claves[c])));
        CLAVES_SESION.forEach((clave) => {
          if (otraPersona && CLAVES_IDENTIDAD.includes(clave)) return;
          reflejar(clave, claves[clave] ?? null);
        });
        if (!conectado) {
          titular = subDe(claves.zuno_access) || null;
          conectado = true;
          clearTimeout(reloj);
          reenviarEscrituras();
          resolve(true);
        } else {
          alCambiar?.(null, null);
        }
      } else if (d.tipo === 'orvian-sesion:cambio' && CLAVES_SESION.includes(d.clave)) {
        // El correo escribe zuno_deleg ANTES que el resto al entrar en una delegación y lo borra ANTES al
        // salir: mientras dura, esta pestaña no toca su sesión (ni el borrado del refresh que la acompaña).
        if (titular && d.clave === 'zuno_deleg') { delegacionAjena = d.valor !== null && d.valor !== undefined; return; }
        if (delegacionAjena && CLAVES_IDENTIDAD.includes(d.clave)) return;
        if (cambiaDePersona(d.clave, d.valor)) return;
        reflejar(d.clave, d.valor);
        alCambiar?.(d.clave, d.valor);
      }
    };

    const reloj = setTimeout(() => {
      if (conectado) return;
      window.removeEventListener('message', alMensaje);
      marco?.remove();
      marco = null;
      CLAVES_SESION.forEach((clave) => reflejar(clave, null));
      resolve(false);
    }, espera);

    window.addEventListener('message', alMensaje);
    marco = document.createElement('iframe');
    marco.src = `${CORREO_URL}${PUENTE()}`;
    marco.title = 'Sesión de Orvian';
    marco.setAttribute('aria-hidden', 'true');
    marco.tabIndex = -1;
    marco.style.cssText = 'position:absolute;width:0;height:0;border:0;visibility:hidden';
    // Por si el aviso de "lista" se perdió: al terminar de cargar se pide de todos modos
    marco.addEventListener('load', pedir);
    document.body.appendChild(marco);
  });
}
